// Modo "Animar imagen" — pantalla propia (ruta /animar-imagen).
//
// Promueve a modo la función que hasta ahora sólo existía como un botón adentro
// del historial (GeneratedImagesHistory) y del visor inline (GeneratedImagesInline),
// donde no la encontraba nadie que no abriera una imagen generada. Suma además un
// caso nuevo: animar una FOTO PROPIA, no sólo lo que generó Atlas.
//
// UI: espeja el layout de los otros modos (PresetDetailSheet) para que se sienta
// parte de la misma familia, aunque sea una ruta y no un sheet:
//   • desktop → panel fijo a la derecha de la sidebar (bg gris + esquinas
//     redondeadas), header con "Volver", y DOS columnas: carátula + texto a la
//     izquierda, card blanca de opciones a la derecha.
//   • mobile  → carátula full-bleed arriba y panel blanco redondeado abajo
//     (-mt-6 rounded-t-3xl), con el back flotante del shell (mobileBackOverride,
//     que además esconde la bottom nav).
//
// Dos pantallas: (1) elegir la imagen — subir foto o elegir una generada;
// (2) configurar y generar — descripción sugerida, duración, múltiples escenas.
// Al generar: pantalla de carga estándar → historial con el video, igual que el
// resto de las generaciones. La foto subida NO se registra en `imagenes_generadas`
// (ver lib/animarImagenUpload). Ver ADR-2026-08-14-modo-animar-imagen.
//
// OJO con los colores: las clases token-con-opacidad (`text-brand-black/90`,
// `bg-brand-primary/10`) NO compilan en este Tailwind — quedan sin regla en el CSS.
// Por eso acá se usa `text-brand-black` pelado y hex arbitrario con alpha
// (`text-[#3C3C43]/60`, `bg-[#1644EB]/10`), que sí compilan.

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ImageUp, Info, Loader2, Sparkles } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { useRegistrarPantalla } from '../../../chat-atlas/PuenteDePantallaContext';
import type { ControladorDePantalla } from '../../../chat-atlas/puenteDePantalla';
import { describirAnimar } from '../puente/camposDeModo';
import { empresaRecordada, recordarEmpresa } from '../empresaDeLaSesion';
import { useSidebar } from '../../../context/SidebarContext';
import { useVolverAtras } from '../../../hooks/useVolverAtras';
// La vuelta atrás del modo viaja en la entrada de historial, no en `sessionStorage`
// (ADR-2026-09-10-los-modos-con-ruta-propia-tambien-vuelven-a-lo-que-generaste).
import {
  configDelModoAlMontar,
  recordarConfigDelModo,
} from './shared/volverAlModo';
import CachedImg from '../../../components/CachedImg';
import LoadingAnimation from '../../../components/LoadingAnimation';
import SubscriptionLimitModal from '../../../components/SubscriptionLimitModal';
import OptionSheet from '../../../components/opciones/OptionSheet';
import { PasoUnoMobile } from './shared/PasoUnoMobile';
import { CreditBadge } from '../../../components/CreditBadge';
import { isProOrUltra } from '../../../lib/planCapabilities';
import { GenerateAdError } from '../../../lib/generateAdRequest';
import { subirImagenParaAnimar } from '../../../lib/animarImagenUpload';
import {
  SUBIDA_REQUIERE_ATLAS,
  buildAnimarVideoBody,
  fetchSuggestedVideoPromptForSource,
  generateVideoFromImage,
  type AnimarSource,
} from '../../../lib/videoFromImage';
import {
  GeneradasPickGrid,
  type GeneradaPick,
} from './shared/GeneradasPickGrid';
import { ShellModoRedisenado } from './shared/ShellModoRedisenado';
import { useColumnaResultadosModo } from './shared/useColumnaResultadosModo';
import { CardPicker } from '../components/PanelCatalogoCompacto';
import { PanelLateralModo } from '../components/PanelLateralModo';
import { nombreCortoModo } from '../modesCatalog';
import { MAX_TANDAS_EN_CURSO } from '../generacionesEnCurso';
import { leerSaldo } from '../../../lib/saldo';

/** Carátula del modo — el mismo clip explicativo que usa el panel actual de Animar. */
const HERO_VIDEO = '/images/video_animar_imagen.mp4';

/** Título y bajada del hero, ARRIBA de la carátula — el mismo tratamiento de Modelo v2
 *  y Catálogo (ADR-2026-09-08-la-muestra-de-los-atajos-encabeza-con-titulo). */
const HERO_TITULO = 'Convertí la foto en video';
const HERO_DESC =
  'Atlas le da movimiento realista a la escena, con la duración que elijas.';

/** Duración por defecto del video, igual que el panel del historial. */
const DURACION_DEFAULT = 8;

/** La ruta del modo — la entrada de historial que se reemplaza al irse a los resultados. */
const RUTA_MODO = '/animar-imagen';

/** Lo que hace falta para reabrir el modo en el paso 2, tal como se generó. */
interface ConfigDeAnimar {
  source: AnimarSource;
  /** La firmada con la que se dibuja el preview. Se guarda en vez de re-firmar: el
   *  "Atrás" pasa segundos después y la URL sigue viva. */
  previewUrl: string | null;
  videoPrompt: string;
  duracion: number;
  multiEscena: boolean;
}
const DURACION_MIN = 4;
const DURACION_MAX = 15;

/** Estimaciones para la barra de la pantalla de carga: Grok escena única tarda
 *  1-3 min; Kling multi-shot, 3-8. */
const CARGA_MS_ESCENA_UNICA = 180_000;
const CARGA_MS_MULTI_ESCENA = 480_000;

/** Cuántas veces se pollea la fila del video antes de mandar al historial igual. */
const POLL_INTENTOS = 15;
const POLL_INTERVALO_MS = 3000;

export default function AnimarImagenMode() {
  const navigate = useNavigate();
  const { sidebarWidth, setMobileBackOverride } = useSidebar();
  const volverAtras = useVolverAtras();
  /**
   * La empresa arranca de la que ya se resolvió en esta pestaña y no en `null` —
   * ADR-2026-09-08-la-portada-no-parpadea-mientras-se-resuelve-la-empresa.
   *
   * Este modo tiene ruta propia, así que al entrar monta de cero y pide su `id_company`
   * con `auth.getUser()` + `profiles`: medio segundo largo en el que la columna de
   * resultados no sabe de quién son las tandas y dibuja esqueletos en lugar de lo que se
   * está generando. `empresaRecordada()` lo devuelve al instante; el efecto de abajo lo
   * confirma igual contra la base.
   */
  const [companyId, setCompanyId] = useState<string | null>(() => empresaRecordada());
  const [brandId, setBrandId] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);

  /**
   * La configuración con la que se volvió de generar, si el "Atrás" trajo una —
   * ADR-2026-09-10-los-modos-con-ruta-propia-tambien-vuelven-a-lo-que-generaste.
   *
   * Se lee UNA vez, al montar, y siembra los estados de las dos pantallas: el modo
   * aparece en su paso 2 con la imagen, la descripción y la duración puestas, en vez de
   * mandar a elegir de nuevo la imagen que se acaba de usar.
   */
  const configAlVolver = useRef(configDelModoAlMontar<ConfigDeAnimar>()).current;

  // ── Pantalla 1: elegir la imagen ──
  const [paso, setPaso] = useState<'elegir' | 'configurar'>(
    configAlVolver ? 'configurar' : 'elegir',
  );
  const [source, setSource] = useState<AnimarSource | null>(
    configAlVolver?.source ?? null,
  );
  const [previewUrl, setPreviewUrl] = useState<string | null>(
    configAlVolver?.previewUrl ?? null,
  );
  const [subiendo, setSubiendo] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sheetGeneradas, setSheetGeneradas] = useState(false);
  /**
   * El panel lateral de desktop, donde se cambia la imagen sin salir de la
   * configuración — ADR-2026-09-07-animar-elige-la-imagen-con-la-card-y-el-panel-lateral.
   * Es el mismo mecanismo con el que Fotos de producto y Catálogo cambian sus productos.
   */
  const [panelImagenAbierto, setPanelImagenAbierto] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  /**
   * ¿Queda grilla abajo del scroll? Prende el degradado del pie del panel (desktop).
   *
   * Se mide en cada scroll y, además, cada vez que CRECE el contenido: la lista de
   * generadas la carga `GeneradasPickGrid` en SU estado, así que cuando llegan las filas
   * este componente no re-renderiza y una medición atada al render se quedaría con la
   * foto del primer paint —grilla vacía, "no hay nada abajo"— para siempre. De ahí el
   * `ResizeObserver` sobre el contenido y no un efecto a secas.
   */
  const scrollElegirRef = useRef<HTMLDivElement | null>(null);
  const contenidoElegirRef = useRef<HTMLDivElement | null>(null);
  const [quedaContenidoAbajo, setQuedaContenidoAbajo] = useState(false);
  const medirFinDelScroll = (el: HTMLDivElement | null) => {
    if (!el) return;
    // 4px de tolerancia: con zoom del browser `scrollTop` queda fraccionario y la resta
    // nunca da exactamente 0 al final.
    setQuedaContenidoAbajo(el.scrollHeight - el.scrollTop - el.clientHeight > 4);
  };
  useEffect(() => {
    const contenido = contenidoElegirRef.current;
    if (!contenido) return;
    const medir = () => medirFinDelScroll(scrollElegirRef.current);
    medir();
    // Observa el CONTENIDO (crece con las filas) y no el scroller, cuyo alto lo fija el
    // panel y no cambia nunca.
    const ro = new ResizeObserver(medir);
    ro.observe(contenido);
    window.addEventListener('resize', medir);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', medir);
    };
  }, [paso]);
  // Cada selección invalida a las anteriores en vuelo: si una subida vieja termina
  // tarde no puede pisar la imagen que el usuario está mirando.
  const uploadSeqRef = useRef(0);

  // ── Pantalla 2: configurar y generar ──
  const [videoPrompt, setVideoPrompt] = useState(configAlVolver?.videoPrompt ?? '');
  const [loadingPrompt, setLoadingPrompt] = useState(false);
  const [duracion, setDuracion] = useState(configAlVolver?.duracion ?? DURACION_DEFAULT);
  const [multiEscena, setMultiEscena] = useState(configAlVolver?.multiEscena ?? false);
  const [generando, setGenerando] = useState(false);
  const [showLoading, setShowLoading] = useState(false);
  const [completeNow, setCompleteNow] = useState(false);
  const [limitModal, setLimitModal] = useState<
    'free-trial-ended' | 'plan-limit-reached' | null
  >(null);
  const pendingNavRef = useRef<{ videoId: string; companyId: string } | null>(null);
  /**
   * El plan que devolvió el último guard de créditos. Lo necesita el modal de límite
   * cuando el que avisa es el BACKEND (la promesa rechaza con un error de billing
   * después de que la tanda ya está en la columna): ahí no hay a quién preguntarle el
   * plan sin otra vuelta a Supabase, y el guard lo acaba de leer.
   */
  const planRef = useRef<string | null>(null);
  const promptPedidoRef = useRef(false);

  // Paridad de cobro con el backend: Kling multi-escena cobra segundos × 2.
  const costo = multiEscena ? duracion * 2 : duracion;

  // El chat puede leer y tocar esta pantalla (ADR-2026-09-10-el-agente-toca-la-pantalla):
  // la descripción del video, la duración y las escenas. La imagen se elige en pantalla.
  useRegistrarPantalla(
    useMemo<ControladorDePantalla>(
      () => ({
        clave: 'animar-imagen',
        describir: () =>
          describirAnimar({
            ruta: '/animar-imagen',
            paso,
            imagenElegida: !!source,
            descripcion: videoPrompt,
            duracion,
            min: DURACION_MIN,
            max: DURACION_MAX,
            multiEscena,
          }),
        aplicar: (cambios) => {
          const aplicados: string[] = [];
          for (const c of cambios) {
            if (c.campo === 'descripcion_video') setVideoPrompt(c.valor as string);
            else if (c.campo === 'duracion') setDuracion(c.valor as number);
            else if (c.campo === 'multi_escena') setMultiEscena(c.valor as boolean);
            else continue;
            aplicados.push(c.campo);
          }
          return { aplicados, fallidos: [] };
        },
      }),
      [paso, source, videoPrompt, duracion, multiEscena],
    ),
  );

  useEffect(() => {
    (async () => {
      const { data: auth } = await supabase.auth.getUser();
      if (!auth.user) return;
      setUserId(auth.user.id);
      const { data: p } = await supabase
        .from('profiles')
        // `current_brand_id` además de la empresa: la columna de resultados lista las
        // creaciones con el MISMO filtro de marca que el historial.
        .select('id_company, current_brand_id')
        .eq('id', auth.user.id)
        .maybeSingle();
      const perfil = p as {
        id_company?: string | null;
        current_brand_id?: string | null;
      } | null;
      setCompanyId(perfil?.id_company ?? null);
      setBrandId(perfil?.current_brand_id ?? null);
      // Que el próximo modo que se abra la tenga sin esperar su propia consulta.
      recordarEmpresa(perfil?.id_company, auth.user.id);
    })();
  }, []);

  const volverAElegir = () => {
    setPaso('elegir');
    setSource(null);
    setPreviewUrl(null);
    setVideoPrompt('');
    setError(null);
    // Que la próxima imagen vuelva a pedir su propia descripción sugerida.
    promptPedidoRef.current = false;
  };

  // Back flotante del shell en mobile. Además de dar el botón, esconde la bottom
  // nav (MobileBottomNav se apaga con `mobileBackOverride !== null`), que es lo
  // que hacen el resto de los modos al ocupar la pantalla entera.
  useEffect(() => {
    setMobileBackOverride(() => () => {
      if (paso === 'configurar') volverAElegir();
      // El back de la PRIMERA pantalla del modo vuelve a donde estabas —
      // ADR-2026-09-07-el-back-de-los-modos-vuelve-a-donde-estabas. Antes iba fijo a
      // `/modos`: entrando desde la home (que es de donde se entra casi siempre), el
      // back te dejaba en una pantalla por la que no habías pasado.
      else volverAtras();
    });
    return () => setMobileBackOverride(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paso, volverAtras]);

  // Descripción sugerida: se pide UNA vez, al entrar a configurar.
  useEffect(() => {
    if (paso !== 'configurar' || !source || !userId || promptPedidoRef.current) {
      return;
    }
    promptPedidoRef.current = true;
    setLoadingPrompt(true);
    fetchSuggestedVideoPromptForSource(source, userId)
      .then((p) => setVideoPrompt(p))
      .catch(() => setVideoPrompt(''))
      .finally(() => setLoadingPrompt(false));
  }, [paso, source, userId]);

  const elegirArchivo = () => fileInputRef.current?.click();

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // permitir re-elegir el mismo archivo
    if (!file) return;
    if (!companyId) {
      setError('No pudimos identificar tu cuenta. Recargá la página.');
      return;
    }
    const seq = ++uploadSeqRef.current;
    setError(null);
    setSubiendo(true);
    try {
      const subida = await subirImagenParaAnimar(file, companyId);
      if (seq !== uploadSeqRef.current) return; // ganó una selección más nueva
      setSource({ tipo: 'subida', bucket: subida.bucket, path: subida.path });
      setPreviewUrl(subida.previewUrl);
      setPanelImagenAbierto(false);
      // Imagen nueva, descripción nueva — mismo motivo que en `elegirGenerada`.
      promptPedidoRef.current = false;
      setVideoPrompt('');
      setPaso('configurar');
    } catch (err) {
      if (seq !== uploadSeqRef.current) return; // el fallo es de una subida vieja
      setError(err instanceof Error ? err.message : 'No se pudo subir la imagen.');
    } finally {
      if (seq === uploadSeqRef.current) {
        setSubiendo(false);
        // El sheet de mobile se cierra al TERMINAR la subida, con o sin éxito —
        // ADR-2026-09-08-la-subida-de-mobile-cierra-el-sheet. Desde que "Subir imagen" es
        // una celda de esa grilla (y no un botón en la pantalla), quien la dispara está
        // ADENTRO del sheet: sin esto la subida corría, la pantalla de atrás avanzaba al
        // paso 2 y el usuario seguía mirando la misma lista, como si no hubiera pasado
        // nada. Y si falla, el aviso que se dibuja abajo tampoco se veía.
        setSheetGeneradas(false);
      }
    }
  };

  const elegirGenerada = (pick: GeneradaPick) => {
    setSource({ tipo: 'generada', imageId: pick.id });
    setPreviewUrl(pick.signedUrl);
    setSheetGeneradas(false);
    setPanelImagenAbierto(false);
    // La descripción sugerida es de ESA imagen: al cambiarla se pide de nuevo, igual que
    // cuando cambiar de imagen era volver al paso 1. Se pierde lo que el usuario haya
    // escrito, que es lo que ya pasaba por ese camino.
    promptPedidoRef.current = false;
    setVideoPrompt('');
    setPaso('configurar');
  };

  /**
   * La COLUMNA DE RESULTADOS del modo — ADR-2026-09-08-animar-y-carrusel-generan-en-la-columna.
   *
   * Lo generado aterriza a la derecha, donde estaba la muestra, igual que en Automática,
   * Modelo, Fotos de producto y Catálogo: generar deja de llevarse la pantalla.
   */
  const { columna, hayColumna, visorAbierto, lanzarTanda, puedeCrear } =
    useColumnaResultadosModo({
      companyId,
      brandId,
      modo: 'animar-imagen',
      onError: setError,
      onLimitePlan: () =>
        setLimitModal(
          planRef.current === 'gratis' ? 'free-trial-ended' : 'plan-limit-reached',
        ),
    });

  const generar = async () => {
    if (!source || !userId || !companyId || generando) return;
    /**
     * En DESKTOP la tanda va a la columna de la derecha y la pantalla no se mueve; abajo
     * de `md` no hay dos columnas (el shell es `hidden md:flex`), así que ahí sigue el
     * camino de siempre: pantalla de carga y el video en el historial.
     */
    const enPanel =
      typeof window !== 'undefined' &&
      window.matchMedia('(min-width: 768px)').matches;
    setGenerando(true);
    setError(null);
    if (!enPanel) {
      setShowLoading(true);
      setCompleteNow(false);
    }
    try {
      // Guards con el dato fresco de Supabase: plan (animar es Pro/Ultra, igual
      // que el botón del historial) y créditos suficientes para la duración.
      const { data: perfil } = await supabase
        .from('company_profiles')
        .select('plan')
        .eq('id_company', companyId)
        .single();
      const plan = (perfil as { plan?: string } | null)?.plan;
      planRef.current = plan ?? null;
      if (plan && !isProOrUltra(plan)) {
        setShowLoading(false);
        setGenerando(false);
        setError('Animar imágenes está disponible para los planes Pro y Ultra.');
        return;
      }
      const restantes = await leerSaldo(companyId).then((s) => s.disponibles).catch(() => undefined);
      if (typeof restantes === 'number' && restantes < costo) {
        setShowLoading(false);
        setGenerando(false);
        setLimitModal(plan === 'gratis' ? 'free-trial-ended' : 'plan-limit-reached');
        return;
      }

      const body = buildAnimarVideoBody(source, {
        userId,
        prompt: videoPrompt,
        duracion,
        multiplesEscenas: multiEscena,
      });

      if (enPanel) {
        // El tope se pregunta ANTES de disparar: acá todavía se puede frenar y decirlo,
        // que es distinto de lo que hace la columna con un pedido ya en vuelo
        // (ADR-2026-09-06-tope-de-tres-tandas-en-curso).
        if (!puedeCrear()) {
          setGenerando(false);
          setError(
            `Ya hay ${MAX_TANDAS_EN_CURSO} generaciones en curso. Esperá a que termine alguna para pedir otra.`,
          );
          return;
        }
        // La promesa se entrega SIN esperarla: los recuadros salen ya y el usuario se
        // queda en el modo, con el panel de la izquierda listo para el próximo video.
        // El `file_name` lo espera `preloadVideosDeTanda`, que es quien lo necesita.
        lanzarTanda({
          total: 1,
          esVideo: true,
          // Lo que va a cobrar de verdad: en un video es la duración, no "1 resultado".
          creditos: costo,
          resultado: generateVideoFromImage(body, { companyId }).then((id) => [id]),
          errorGenerico: 'No se pudo generar el video. Intentá nuevamente.',
        });
        setGenerando(false);
        return;
      }

      const videoId = await generateVideoFromImage(body, { companyId });

      // La fila del video puede tardar en tener `file_name`: esperamos un poco
      // antes de mandar al historial, igual que el botón Animar de siempre.
      for (let intento = 1; intento <= POLL_INTENTOS; intento++) {
        const { data: fila } = await supabase
          .from('imagenes_generadas')
          .select('file_name')
          .eq('id', videoId)
          .eq('id_company', companyId)
          .single();
        if (fila?.file_name) break;
        await new Promise((r) => setTimeout(r, POLL_INTERVALO_MS));
      }

      pendingNavRef.current = { videoId, companyId };
      // La vuelta atrás: el "Atrás" desde los resultados devuelve ESTA configuración, no
      // la pantalla de elegir imagen.
      recordarConfigDelModo<ConfigDeAnimar>(navigate, RUTA_MODO, {
        source,
        previewUrl,
        videoPrompt,
        duracion,
        multiEscena,
      });
      setCompleteNow(true); // cierra la barra de carga → onCompleted navega
    } catch (e) {
      console.error('[AnimarImagenMode] generar falló', e);
      setShowLoading(false);
      setGenerando(false);
      setError(
        e instanceof GenerateAdError && e.isActionable
          ? e.message
          : 'No se pudo generar el video. Intentá nuevamente.',
      );
    }
  };

  // ── Piezas compartidas entre mobile y desktop ────────────────────────────

  const heroVideo = (className: string) => (
    <video
      src={HERO_VIDEO}
      autoPlay
      loop
      muted
      playsInline
      preload="metadata"
      aria-label="Ejemplo del modo Animar imagen"
      className={className}
    />
  );

  const errorBox = error ? (
    <div className="mt-3 rounded-lg bg-error-soft text-error-text text-[12.5px] px-3 py-2">
      {error}
    </div>
  ) : null;

  /**
   * MOBILE, paso 1: la anatomía del modo Modelo —
   * ADR-2026-09-08-todos-los-modos-abren-igual-en-mobile. Título, bajada, la carátula
   * —que en este modo es un video— y UNA card al pie, en vez del panel blanco con
   * "Subir imagen" + "Elegir de mis generadas".
   *
   * "Subir imagen" no se pierde: vive adentro del sheet que abre la card, como primera
   * celda de la grilla, que es el lugar que ya ocupa en el panel de escritorio.
   */
  const pantallaMobileComoModelo = (
    <PasoUnoMobile
      titulo="Elegí la imagen para animar"
      bajada="Atlas le va a dar movimiento a esta foto y devolverte un video."
      portada={heroVideo(
        'max-w-[400px] max-h-full w-full h-auto object-contain rounded-2xl',
      )}
      Icono={ImageUp}
      cardTexto="Elegí tu imagen"
      onAbrir={() => setSheetGeneradas(true)}
      aviso={errorBox}
    />
  );

  /** Opciones del video (pantalla 2). Igual en las dos variantes. */
  const bloqueConfigurar = (
    <>
      <p className="text-[13px] text-[#3C3C43]/60 uppercase tracking-wide mb-2">
        Descripción del video
      </p>
      <div className="rounded-[13px] bg-white overflow-hidden border border-neutral-200">
        {loadingPrompt ? (
          // Misma barra que el panel de Animar del historial / visor inline: la
          // sugerencia tarda ~10-15s y sin barra el spinner solo se siente colgado.
          // La animación es de duración fija (no refleja progreso real, igual que
          // el original) y `forwards` la deja llena si tarda de más.
          <div className="px-4 py-4">
            <div className="flex items-center gap-2 mb-3">
              <Loader2 className="w-5 h-5 text-brand-primary animate-spin" />
              <span className="text-[15px] text-[#3C3C43]/60">
                Generando idea para el video…
              </span>
            </div>
            <div className="w-full h-1 bg-black/[0.06] rounded-full overflow-hidden">
              <div
                className="h-full bg-brand-primary rounded-full"
                style={{ animation: 'videoPromptProgress 15s linear forwards' }}
              />
            </div>
            <style>{`@keyframes videoPromptProgress { from { width: 0%; } to { width: 100%; } }`}</style>
          </div>
        ) : (
          <div className="px-4 py-3">
            <textarea
              rows={4}
              value={videoPrompt}
              onChange={(e) => setVideoPrompt(e.target.value)}
              placeholder="Ej: que la cámara se acerque despacio mientras la luz cambia…"
              className="w-full text-[15px] text-brand-black placeholder:text-[#3C3C43]/30 bg-transparent resize-none focus:outline-none leading-relaxed"
            />
          </div>
        )}
      </div>
      <p className="text-[13px] text-[#3C3C43]/60 mt-2">
        Cuanto más específico seas, mejor va a salir.
      </p>

      <p className="text-[13px] text-[#3C3C43]/60 uppercase tracking-wide mt-6 mb-2">
        Configuración
      </p>
      <div className="rounded-[13px] bg-white overflow-hidden border border-neutral-200">
        {/* Duración */}
        <div className="px-4 py-[11px]">
          <div className="flex items-center justify-between mb-3">
            <span className="text-[15px] text-brand-black">Duración</span>
            <span className="text-[15px] font-medium text-brand-black tabular-nums">
              {duracion} seg
            </span>
          </div>
          <input
            type="range"
            min={DURACION_MIN}
            max={DURACION_MAX}
            step={1}
            value={duracion}
            onChange={(e) => setDuracion(Number(e.target.value))}
            aria-label="Duración del video en segundos"
            className="w-full accent-brand-primary cursor-pointer"
          />
          <div className="flex items-center gap-2 mt-2">
            <Info className="w-3.5 h-3.5 text-brand-primary shrink-0" />
            <span className="text-[13px] text-[#3C3C43]/60">
              Se consumirán {costo} imágenes
            </span>
          </div>
        </div>

        <div className="h-px bg-black/[0.08]" />

        {/* Múltiples escenas */}
        <div className="px-4 py-[11px]">
          <div className="flex items-center justify-between">
            <span className="text-[15px] text-brand-black">Múltiples escenas</span>
            <button
              type="button"
              onClick={() => setMultiEscena(!multiEscena)}
              aria-pressed={multiEscena}
              aria-label="Múltiples escenas"
              className={`relative w-[51px] h-[31px] rounded-full transition-colors duration-200 ${
                multiEscena ? 'bg-brand-primary' : 'bg-neutral-300'
              }`}
            >
              <span
                className={`absolute top-[2px] left-[2px] w-[27px] h-[27px] rounded-full bg-white shadow-sm transition-transform duration-200 ${
                  multiEscena ? 'translate-x-[20px]' : 'translate-x-0'
                }`}
              />
            </button>
          </div>
          {multiEscena && (
            <p className="text-[13px] text-[#3C3C43]/60 mt-2">
              El video se divide en varias escenas con transiciones automáticas.
              Consume el doble de imágenes.
            </p>
          )}
        </div>
      </div>
      {errorBox}
    </>
  );

  /** El botón Generar. Lo comparten los dos pies —el de mobile y el del panel—: lo
   *  único que cambia entre ellos es el aire alrededor. */
  const botonGenerar = (
    <button
      type="button"
      onClick={generar}
      disabled={generando}
      className="w-full inline-flex items-center justify-center gap-2 py-3.5 px-6 rounded-2xl bg-brand-primary hover:bg-brand-primary-hover disabled:opacity-40 disabled:cursor-not-allowed text-white text-[15.5px] font-semibold shadow-sm transition-colors"
    >
      <Sparkles className="w-5 h-5" />
      Generar video
      <CreditBadge credits={costo} tone="on-primary" size="sm" />
    </button>
  );

  /** Pie de MOBILE: sticky al fondo del panel blanco de abajo. */
  const pieGenerarMobile = (
    <div className="shrink-0 bg-white px-5 pt-3 pb-5 border-t border-black/[0.06]">
      {botonGenerar}
    </div>
  );

  /** Pie del PANEL de desktop — el mismo que el de los otros modos del rediseño:
   *  filete de 1px arriba y el CTA a todo el ancho de la card. */
  const pieGenerarPanel = (
    <div className="shrink-0 px-6 pt-3 pb-4 bg-white border-t border-black/[0.05]">
      {botonGenerar}
    </div>
  );

  // ── Piezas del DESKTOP: el diseño de Automática / Modelo / Catálogo ───────
  // ADR-2026-09-07-animar-imagen-toma-el-diseno-de-los-modos. El marco y las dos
  // columnas los pone `ShellModoRedisenado`; acá va lo de adentro.

  /**
   * "Subir imagen" como PRIMERA CELDA de la grilla, no como botón arriba de ella.
   *
   * Es el lugar que ocupa "Subir nuevo producto" en la grilla de productos de los otros
   * modos, y por el mismo motivo: subir una foto y elegir una generada son dos formas de
   * responder la MISMA pregunta —de dónde sale la imagen—, así que van en la misma
   * lista. Toma la forma de las celdas de al lado (cuadrada, `rounded-[13px]`) para que
   * se lea como una celda más y no como un botón que se coló en la grilla.
   *
   * El chip del ícono va en hex con alpha y no en `bg-brand-primary/10`: el token de
   * marca está declarado sin `<alpha-value>`, así que el modificador de opacidad no
   * emite ninguna regla y el chip queda transparente — es lo que le pasa hoy a la celda
   * de "Subir nuevo producto" de los otros modos.
   */
  const celdaSubirImagen = (
    <button
      type="button"
      onClick={elegirArchivo}
      disabled={subiendo}
      className="relative aspect-square rounded-[13px] overflow-hidden bg-white border-2 border-dashed border-neutral-300 hover:border-brand-primary hover:bg-[rgb(var(--brand-primary-rgb)_/_0.03)] disabled:opacity-60 disabled:cursor-not-allowed active:scale-[0.98] transition-all flex flex-col items-center justify-center gap-2 px-2"
    >
      <span className="w-9 h-9 rounded-xl bg-[rgb(var(--brand-primary-rgb)_/_0.1)] flex items-center justify-center">
        {subiendo ? (
          <Loader2 className="w-5 h-5 text-brand-primary animate-spin" />
        ) : (
          <ImageUp className="w-5 h-5 text-brand-primary" />
        )}
      </span>
      <span className="text-[11.5px] font-bold text-brand-primary text-center leading-tight">
        {subiendo ? 'Subiendo…' : 'Subir imagen'}
      </span>
    </button>
  );

  /** Paso 1 en el panel: el set de imágenes del que sale la que se va a animar. */
  /**
   * La grilla del modo: subir + las generadas. La MISMA en los dos lugares donde se
   * elige la imagen —el paso 1, en el panel, y el panel lateral del paso 2—, que es lo
   * que hace que cambiar la imagen sea el mismo gesto que elegirla.
   */
  const grillaDeImagenes = (
    <GeneradasPickGrid
      companyId={companyId}
      // En el paso 2 la que está en uso va tildada: el panel se abre para cambiarla, así
      // que tiene que decir cuál es la de ahora.
      selectedId={source?.tipo === 'generada' ? source.imageId : null}
      onSelect={elegirGenerada}
      // Con el flag de rollback a n8n no hay forma de animar una foto suelta: ahí la
      // celda de subir no va y queda sólo el camino de las generadas.
      tileInicial={SUBIDA_REQUIERE_ATLAS ? undefined : celdaSubirImagen}
      emptyHint="Todavía no generaste ninguna imagen. Subí una foto para animarla."
    />
  );

  const panelElegir = (
    <>
      {/* UN solo texto y no un título con su bajada: el bloque decía dos veces lo mismo
          —"elegí la imagen para animar" y después "Atlas le va a dar movimiento a esta
          foto"— y entre los dos empujaban la grilla media pantalla para abajo. Los otros
          modos tienen exactamente una línea acá ("Elegí uno o varios productos"), en la
          misma medida. */}
      <div className="shrink-0 px-6 flex justify-between gap-2 items-baseline pt-5 pb-1">
        <p className="text-[17px] font-semibold text-brand-black leading-tight">
          Elegí la imagen que Atlas va a convertir en video
        </p>
      </div>
      <div className="relative flex-1 min-h-0 flex flex-col">
        <div
          ref={scrollElegirRef}
          onScroll={(e) => medirFinDelScroll(e.currentTarget)}
          className="flex-1 min-h-0 overflow-y-auto px-6 pt-4"
        >
          <div ref={contenidoElegirRef}>
            {grillaDeImagenes}
            {errorBox}
            <div className="h-3" />
          </div>
        </div>
        {/* Degradado del pie: la última fila se desvanece en vez de cortarse contra el
            borde, que es lo que hace leer la grilla como terminada. Se apaga al llegar
            abajo — si quedara siempre, dejaría de significar "hay más". */}
        {quedaContenidoAbajo && (
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-14 bg-gradient-to-t from-white via-white/85 to-transparent" />
        )}
      </div>
    </>
  );

  /** Paso 2 en el panel: la imagen elegida, las opciones del video y el Generar. */
  const panelConfigurar = (
    <>
      {/* La imagen elegida encabeza la configuración con la MISMA card con la que Fotos
          de producto y Catálogo encabezan la suya con los productos (`CardPicker`):
          miniatura, qué es, y el chevron que anuncia que se puede cambiar. Reemplaza al
          par "Configurá el video" + un link "Cambiar imagen" — el título nombraba el
          paso en el que ya se está (lo dice el `<h1>` de arriba) y el link era la única
          pista de que la imagen se podía tocar, escrita como texto suelto.
          Tocarla abre el panel lateral, sin salir de la configuración: antes cambiar de
          imagen era volver al paso 1. */}
      <div className="shrink-0 px-6 pt-5 pb-1">
        <CardPicker
          etiqueta="Imagen"
          valor={
            source?.tipo === 'subida' ? 'Foto que subiste' : 'De tus creaciones'
          }
          imagenes={previewUrl ? [previewUrl] : []}
          onClick={() => setPanelImagenAbierto(true)}
        />
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto px-6 pt-3 pb-4">
        {bloqueConfigurar}
      </div>
      {pieGenerarPanel}
    </>
  );

  const overlays = (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={onFile}
      />
      <OptionSheet
        open={sheetGeneradas}
        onClose={() => setSheetGeneradas(false)}
        title="Mis imágenes generadas"
        hideAccept
      >
        {/* La MISMA grilla del panel de escritorio: "Subir imagen" es su primera celda,
            que es donde vive ese camino desde que la pantalla dejó de tener dos CTA. */}
        {grillaDeImagenes}
      </OptionSheet>
      {showLoading && (
        <LoadingAnimation
          message="Generando video…"
          subMessage="Esto puede tardar unos minutos."
          durationMs={multiEscena ? CARGA_MS_MULTI_ESCENA : CARGA_MS_ESCENA_UNICA}
          completeNow={completeNow}
          onCompleted={() => {
            setShowLoading(false);
            setGenerando(false);
            const pend = pendingNavRef.current;
            pendingNavRef.current = null;
            if (pend) {
              navigate('/redes-sociales/imagenes-generadas', {
                state: { imageIds: [pend.videoId], companyId: pend.companyId },
              });
            }
          }}
        />
      )}
      <SubscriptionLimitModal
        isOpen={limitModal !== null}
        type={limitModal ?? 'plan-limit-reached'}
        onClose={() => setLimitModal(null)}
      />
    </>
  );

  const enConfigurar = paso === 'configurar' && !!source && !!previewUrl;

  return (
    <>
      {/* ── MOBILE ────────────────────────────────────────────────────────── */}
      {!enConfigurar ? (
        pantallaMobileComoModelo
      ) : (
        /* PASO 2: la imagen elegida a sangre y el panel blanco con la configuración. Ese
           paso no cambió — lo que se mira ahí es la foto, no una lista. */
        <div className="md:hidden fixed inset-0 z-30 bg-surface-mobile flex flex-col">
          <div className="relative flex-1 min-h-0 bg-neutral-100">
            <CachedImg
              src={previewUrl as string}
              alt=""
              className="absolute inset-0 w-full h-full object-contain"
            />
          </div>
          <div
            className="relative z-10 bg-white shrink-0 -mt-6 rounded-t-3xl flex flex-col overflow-hidden"
            style={{ maxHeight: '78dvh' }}
          >
            <div className="flex-1 min-h-0 overflow-y-auto px-5 pt-7 pb-6">
              {bloqueConfigurar}
            </div>
            {pieGenerarMobile}
          </div>
        </div>
      )}

      {/* ── DESKTOP: el diseño de los modos — panel a la izquierda, muestra a la
          derecha (ADR-2026-09-07-animar-imagen-toma-el-diseno-de-los-modos) ──── */}
      <ShellModoRedisenado
        sidebarWidth={sidebarWidth}
        // Los mismos dos niveles que arman los otros modos: la home y el modo. El paso
        // NO se suma como tercer nivel —la cabecera del panel no cambia de paso
        // (ADR-2026-09-06-el-encabezado-del-panel-no-cambia-de-paso)—; volver al paso 1
        // es el "Cambiar imagen" del propio panel.
        //
        // El nombre sale de `nombreCortoModo` y no de una constante acá: es la misma
        // tabla que usan el carrusel de la home, la sidebar y el sheet de Generar, así
        // que el modo se llama igual en las cuatro superficies
        // (ADR-2026-08-03-un-solo-nombre-por-modo).
        migas={[
          { label: 'Inicio', onClick: () => navigate('/interfaz-v2') },
          { label: nombreCortoModo('animar-imagen', 'Animar imagen') },
        ]}
        titulo={nombreCortoModo('animar-imagen', 'Animar imagen')}
        panel={enConfigurar ? panelConfigurar : panelElegir}
        // Cambiar la imagen sin salir de la configuración: la misma capa con la que
        // Fotos de producto y Catálogo cambian sus productos. Sólo en el paso 2 — en el
        // 1 la grilla ya ES la pantalla.
        panelLateral={({ contenedorRef, offsetIzquierdo }) => (
          <PanelLateralModo
            abierto={enConfigurar && panelImagenAbierto}
            onCerrar={() => setPanelImagenAbierto(false)}
            titulo="Elegí la imagen"
            offsetIzquierdo={offsetIzquierdo}
            contenedorRef={contenedorRef}
          >
            <div className="flex-1 min-h-0 overflow-y-auto px-6 pb-6 pt-2">
              <p className="text-[13.5px] text-[#3C3C43]/60 leading-relaxed pb-3">
                Subí una foto nueva o elegí otra de tus creaciones. El panel se cierra al
                elegir.
              </p>
              {grillaDeImagenes}
              {errorBox}
            </div>
          </PanelLateralModo>
        )}
        // Con resultados en la columna, ésta se sale de los paddings y llega al marco —
        // el mismo tratamiento que en los modos de `InterfazV2`
        // (ADR-2026-09-06-la-columna-de-resultados-llega-al-marco).
        muestraAlBorde={hayColumna}
        // El visor de una creación es un lightbox portaleado al body: sin bajarle el z a
        // este panel queda debajo y sólo se ve su velo.
        cederZIndex={visorAbierto}
        muestra={
          columna ?? (
          <div
            // `key` por paso: la columna se remonta y hace su entrada. Es la misma
            // animación que usan los otros modos al cambiar de paso.
            key={enConfigurar ? 'muestra-elegida' : 'muestra-hero'}
            className="flex-1 min-h-0 flex flex-col items-center justify-center gap-2 animate-paso-adelante"
          >
            {enConfigurar ? (
              <CachedImg
                src={previewUrl as string}
                alt=""
                className="max-w-[560px] max-h-[480px] w-auto h-auto object-contain rounded-2xl"
              />
            ) : (
              <>
                {/* El texto ENCABEZA la muestra en vez de ir de pie de foto — el mismo
                    tratamiento de Modelo v2 y Catálogo
                    (ADR-2026-08-21-modelo-v2-la-muestra-se-queda): título en negrita
                    arriba y la bajada debajo, para que se lea como el título de lo que
                    el modo hace y no como el epígrafe de la imagen. */}
                <div className="shrink-0 text-center max-w-[440px] mb-3">
                  <h2 className="text-[19px] font-bold leading-tight tracking-[-0.01em] text-brand-black/90">
                    {HERO_TITULO}
                  </h2>
                  <p className="text-[15.5px] text-[#3C3C43]/60 leading-relaxed mt-1">
                    {HERO_DESC}
                  </p>
                </div>
                {heroVideo(
                  'max-w-[560px] max-h-[480px] w-auto h-auto object-contain rounded-2xl',
                )}
              </>
            )}
          </div>
          )
        }
      />

      {overlays}
    </>
  );
}
